import { useGetSubscription, useUpdateSubscription } from "@/api/stripe";
import { STACKED_DIALOG_Z } from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrencyFromCents } from "@/utils/currency";
import React from "react";
import toast from "react-hot-toast";

interface EndTrialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms ending a trial early. The card already on file is charged for the
 * new billing period immediately; the amount shown is the seat total (seat
 * price × seats). The subscription is trialing with a payment method attached,
 * so the update just flips trial_end_now.
 */
export const EndTrialDialog: React.FC<EndTrialDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { data: subscription } = useGetSubscription();
  const updateSubscription = useUpdateSubscription();

  const current = subscription?.data.stripe_subscription;
  const chargeAmount =
    current != null ? current.seat_price * current.seat_count : null;
  const formattedChargeAmount =
    chargeAmount != null
      ? formatCurrencyFromCents(chargeAmount, {
          hideCentsForWholeNumbers: true,
        })
      : null;

  const handleEndTrial = async () => {
    if (!current) {
      return;
    }
    try {
      await updateSubscription.mutateAsync({
        plan_name: current.plan_name,
        billing_interval: current.billing_interval,
        seat_count: current.seat_count,
        trial_end_now: true,
      });
      toast.success("Your trial has ended and your subscription is active.");
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to end trial. Please try again.");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className={STACKED_DIALOG_Z}
        overlayClassName={STACKED_DIALOG_Z}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>End your trial now?</AlertDialogTitle>
          <AlertDialogDescription>
            The payment method on file will be charged
            {formattedChargeAmount ? ` ${formattedChargeAmount}` : ""} to start
            your subscription immediately, and your billing period begins today.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={updateSubscription.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleEndTrial();
            }}
            disabled={updateSubscription.isPending}
          >
            {updateSubscription.isPending ? "Ending trial…" : "End trial now"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
